import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText,
    InputLabel, MenuItem, Paper, Select, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography,
} from "@mui/material";
import {AltRoute} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import BetaBadge from "./layout/BetaBadge";
import NoDataLabel from "./layout/NoDataLabel";
import TableHeader from "./layout/table/TableHeader";
import Constants from "../../utils/constants";
import {toastHelper} from "../../utils/toast-helper";
import ClientSelectorV2 from "./patrimoine/ClientSelectorV2";
import {useGetClientsQuery} from "../../modules/users/clients.api";
import {useGetPatrimoinesLightByClientQuery} from "../../modules/patrimoines/patrimoines.api";
import {useGetScenariosQuery, useImportCalendrierMutation} from "../../modules/simulations/simulations.api";
import {
    formatEuros, texteCalendrier, formatHeures, formatKm, lignesEnCours, lignesScenarios, pollingScenarios,
    resumeContraint, resumeEquipes,
} from "./simulations/simulationRules";
import ModalImportScenario from "./simulations/ModalImportScenario";
import BadgeImporte from "./simulations/BadgeImporte";
import {useEtatCalendrier} from "./simulations/useEtatCalendrier";
import {
    messagePatrimoines, paramsUrl, patrimoineDeLaListe, patrimoinesDuClient, selectionApresClient,
} from "./simulations/atelierRules";

const HEAD_CELLS = [
    {id: 'libelle', label: 'Libellé'},
    {id: 'equipes', label: 'Équipes'},
    {id: 'contraint', label: 'Contraint'},
    {id: 'journees', label: 'Journées (horizon)'},
    {id: 'heures', label: 'Heures'},
    {id: 'km', label: 'Km'},
    {id: 'bacs', label: 'Bacs'},
    {id: 'nonAffectees', label: 'Non affectés'},
    {id: 'cout', label: 'Coût'},
    {id: 'signal', label: 'Signal'},
];

const TAILLE_MAX_OCTETS = 2 * 1024 * 1024; // limite de l'API (413 au-delà)

/** Modale d'import du calendrier de collecte : tout ou rien, 422 → liste ligne / colonne / message. */
function ImportCalendrierDialog({open, patrimoine, onClose}) {
    const [file, setFile] = useState(null);
    const [violations, setViolations] = useState([]);
    const [importCalendrier, {isLoading}] = useImportCalendrierMutation();

    const fermer = () => {
        setFile(null);
        setViolations([]);
        onClose();
    };

    const envoyer = async () => {
        setViolations([]);
        if (file.size > TAILLE_MAX_OCTETS) {
            toastHelper.handleError(null, "Fichier trop volumineux (2 Mo maximum).");
            return;
        }
        try {
            const res = await importCalendrier({patrimoine, file}).unwrap();
            toastHelper.displaySuccess(`Calendrier importé (${res.lignes} lignes).`);
            fermer();
        } catch (e) {
            if (e?.status === 422 && Array.isArray(e.data?.violations)) {
                setViolations(e.data.violations);
            } else {
                toastHelper.handleError(e, e?.data?.error || "Oops, une erreur est survenue.");
            }
        }
    };

    return (
        <Dialog open={open} onClose={fermer} fullWidth maxWidth="sm">
            <DialogTitle>Importer un calendrier de collecte</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{mb: 2}}>
                    Fichier CSV au gabarit (séparateur « ; », UTF-8) : pav, flux, jour, frequence, rang, puis
                    heure_estimee, circuit, depart_circuit, cadence. L'import remplace le calendrier du patrimoine ;
                    une ligne invalide refuse le fichier entier.
                </Typography>
                <Button component="label" variant="outlined" color="primary">
                    {file ? file.name : "Choisir un fichier .csv"}
                    <input type="file" hidden accept=".csv,text/csv"
                           onChange={(e) => {
                               setFile(e.target.files?.[0] || null);
                               setViolations([]);
                           }}/>
                </Button>
                {violations.length > 0 &&
                    <Alert severity="error" sx={{mt: 2}}>
                        <Typography variant="body2" sx={{fontWeight: 600}}>
                            Fichier refusé : {violations.length} erreur{violations.length > 1 ? 's' : ''}, rien n'a été importé.
                        </Typography>
                        <Table size="small" sx={{mt: 1}} aria-label="erreurs d'import">
                            <TableBody>
                                {violations.map((v, i) => (
                                    <TableRow key={i}>
                                        <TableCell>Ligne {v.ligne ?? '—'}</TableCell>
                                        <TableCell>{v.colonne ?? '—'}</TableCell>
                                        <TableCell>{v.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <Button color="primary" onClick={fermer}>Annuler</Button>
                <Button color="secondary" variant="contained" sx={{color: 'white'}} disabled={!file || isLoading}
                        onClick={envoyer}
                        startIcon={isLoading ? <CircularProgress size={22} color="inherit"/> : null}>
                    Importer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function SimulationsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const clientId = searchParams.get('client');
    const patrimoineId = searchParams.get('patrimoine');
    const [importOpen, setImportOpen] = useState(false);
    const [importScenarioOpen, setImportScenarioOpen] = useState(false);

    // Entrée par client (A-1) : plus jamais la liste de tous les patrimoines.
    const {data: clients = [], isLoading: clientsLoading} = useGetClientsQuery();
    const {data: patrimoinesBruts = [], isFetching: patrimoinesLoading} = useGetPatrimoinesLightByClientQuery(clientId, {skip: !clientId});
    const patrimoines = useMemo(() => patrimoinesDuClient(patrimoinesBruts, clientId), [patrimoinesBruts, clientId]);
    const patrimoine = useMemo(() => patrimoineDeLaListe(patrimoines, patrimoineId), [patrimoines, patrimoineId]);
    const messagePat = messagePatrimoines(clientId, patrimoines, patrimoinesLoading);

    // Polling 5 s tant qu'un scénario est pending/processing (U-2) ; 0 = pas de polling.
    // L'intervalle dépend de la dernière réponse : état recalculé à chaque nouvelle donnée.
    const [polling, setPolling] = useState(0);
    const {data, isLoading: scenariosLoading, error: scenariosError} = useGetScenariosQuery(patrimoineId, {
        skip: !patrimoineId,
        pollingInterval: polling,
    });
    useEffect(() => {
        setPolling(pollingScenarios(data));
    }, [data]);

    const {loading: dispoLoading, erreur: dispoErreur, present: aCalendrier, details: calendrier} = useEtatCalendrier(patrimoineId);

    const lignes = useMemo(() => lignesScenarios(data?.scenarios), [data]);
    const enCours = useMemo(() => lignesEnCours(data?.enCours), [data]);

    const choisirClient = (c) => {
        const sel = selectionApresClient({client: clientId, patrimoine: patrimoineId}, c?.id);
        setSearchParams(paramsUrl(sel).slice(1), {replace: true});
    };
    const choisirPatrimoine = (p) => {
        setSearchParams(paramsUrl({client: clientId, patrimoine: p?.id}).slice(1), {replace: true});
    };

    const ouvrir = (id) => navigate(Constants.APP_ROUTE_SIMULATION.replace(':id', id) + paramsUrl({client: clientId, patrimoine: patrimoineId}));
    const dupliquer = (id) => navigate(Constants.APP_ROUTE_SIMULATIONS_ATELIER + paramsUrl({client: clientId, patrimoine: patrimoineId, from: id}));
    const ouvrirAtelier = () => navigate(Constants.APP_ROUTE_SIMULATIONS_ATELIER + paramsUrl({client: clientId, patrimoine: patrimoineId}));

    return (
        <>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center">
                    <PageTitle title="Simulation de découpage" icon={<AltRoute/>}/>
                    <BetaBadge/>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button color="primary" variant="outlined" sx={{borderRadius: 999}} onClick={() => setImportScenarioOpen(true)}>
                        Importer un scénario
                    </Button>
                    <Button color="secondary" variant="contained" sx={{color: 'white', borderRadius: 999}}
                            disabled={!patrimoineId} onClick={ouvrirAtelier}>
                        Ouvrir l'atelier
                    </Button>
                </Stack>
            </Stack>

            <div className="sectionContainer">
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} alignItems={{md: 'flex-start'}}>
                    <Box sx={{width: 420, maxWidth: '100%'}}>
                        <ClientSelectorV2 inline clients={clients} clientSelected={clientId} loading={clientsLoading}
                                          onClientSelected={(id) => choisirClient({id})}/>
                    </Box>
                    <FormControl size="small" sx={{width: 420, maxWidth: '100%'}} disabled={!clientId}>
                        <InputLabel id="simu-patrimoine-label">Patrimoine</InputLabel>
                        <Select labelId="simu-patrimoine-label" label="Patrimoine" value={patrimoine ? String(patrimoine.id) : ''}
                                onChange={(e) => choisirPatrimoine({id: e.target.value})}>
                            {patrimoines.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.libelle}</MenuItem>)}
                        </Select>
                        {messagePat && <FormHelperText>{messagePat}</FormHelperText>}
                    </FormControl>
                </Stack>

                {patrimoineId &&
                    <Stack direction="row" alignItems="center" spacing={2} sx={{mt: 2}}>
                        {dispoLoading
                            ? <Skeleton variant="text" width={360}/>
                            : <Typography variant="body2" data-testid="bandeau-calendrier">
                                {dispoErreur
                                    ? "Impossible de vérifier le calendrier de collecte (erreur de chargement)."
                                    : aCalendrier
                                        ? texteCalendrier(calendrier)
                                        : "Aucun calendrier : seules les simulations libres sont possibles."}
                            </Typography>}
                        <Button color="primary" onClick={() => setImportOpen(true)}>
                            {aCalendrier ? "Remplacer le calendrier" : "Importer un calendrier"}
                        </Button>
                    </Stack>
                }
            </div>

            {!patrimoineId &&
                <NoDataLabel label="Choisissez un client puis un patrimoine pour comparer ses scénarios."/>
            }

            {patrimoineId && scenariosLoading &&
                <Stack spacing={1} sx={{mt: 2}}>
                    <Skeleton variant="rectangular" height={40}/>
                    <Skeleton variant="rectangular" height={40}/>
                    <Skeleton variant="rectangular" height={40}/>
                </Stack>
            }

            {patrimoineId && scenariosError &&
                <Alert severity="error" sx={{mt: 2}}>
                    {scenariosError.status === 404 ? "Patrimoine introuvable." : "Impossible de charger les scénarios."}
                </Alert>
            }

            {patrimoineId && data &&
                <TableContainer component={Paper} variant="outlined" className="mt-2">
                    <Table size="small" aria-label="scénarios de simulation">
                        <TableHeader headCells={HEAD_CELLS}/>
                        <TableBody>
                            {enCours.map((s) => (
                                <TableRow key={`en-cours-${s.id}`}
                                          sx={s.grise ? {opacity: 0.55} : undefined}>
                                    <TableCell>{s.libelle}<BadgeImporte simulation={s} sx={{ml: 1}}/></TableCell>
                                    <TableCell colSpan={9}>
                                        {s.grise &&
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CircularProgress size={22}/>
                                                <Typography variant="body2">
                                                    {s.statut === 'processing' ? "Calcul en cours…" : "En attente du calcul…"}
                                                </Typography>
                                            </Stack>}
                                        {s.echec &&
                                            <Typography variant="body2" sx={{color: 'red.main'}}>
                                                Échec du calcul{s.erreur ? ` : ${s.erreur}` : ''}
                                            </Typography>}
                                    </TableCell>
                                    <TableCell/>
                                </TableRow>
                            ))}
                            {lignes.map((s) => (
                                <TableRow key={s.id} hover style={{cursor: 'pointer'}} onClick={() => ouvrir(s.id)}>
                                    <TableCell>
                                        {s.libelle}
                                        <BadgeImporte simulation={s} sx={{ml: 1}}/>
                                        {s.moinsCher &&
                                            <Chip label="le moins cher" size="small" color="secondary"
                                                  sx={{ml: 1, color: 'white'}}/>}
                                    </TableCell>
                                    <TableCell>{resumeEquipes(s)}</TableCell>
                                    <TableCell>{resumeContraint(s)}</TableCell>
                                    <TableCell>{s.journees ?? '—'}{s.horizon != null ? ` (${s.horizon})` : ''}</TableCell>
                                    <TableCell>{formatHeures(s.heuresTravail)}</TableCell>
                                    <TableCell>{formatKm(s.km)}</TableCell>
                                    <TableCell>{s.bacs ?? '—'}</TableCell>
                                    <TableCell>{s.nonAffectees}</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>{formatEuros(s.cout)}</TableCell>
                                    <TableCell>{s.signal ?? ''}</TableCell>
                                    <TableCell align="center">
                                        <Button color="primary" size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dupliquer(s.id);
                                                }}>
                                            Dupliquer
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {lignes.length === 0 && enCours.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={HEAD_CELLS.length + 1} align="center">
                                        <NoDataLabel label="Aucun scénario enregistré pour ce patrimoine. Jouez-en un dans l'atelier."/>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            }

            <Typography variant="caption" sx={{display: 'block', mt: 2}}>
                Simulation : ne crée rien dans l'ERP.
            </Typography>

            <ModalImportScenario open={importScenarioOpen} onClose={() => setImportScenarioOpen(false)}
                         onImporte={(cree) => {
                             const p = cree?.patrimoine?.id ?? patrimoineId;
                             const c = cree?.patrimoine?.client?.id ?? null;
                             setSearchParams(paramsUrl({client: c, patrimoine: p}).slice(1), {replace: true});
                         }}/>

            {patrimoineId &&
                <ImportCalendrierDialog open={importOpen} patrimoine={patrimoineId} onClose={() => setImportOpen(false)}/>
            }
        </>
    );
}

export default SimulationsPage;
